import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import Avatar from '../components/Avatar';
import WishGiftCard from '../components/WishGiftCard';
import WishFormModal from '../components/WishFormModal';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
    getChild, listChildWishes, addCarer, removeCarer, isCarer,
} from '../services/children';
import { nextBirthday, countdownLabel } from '../utils/dates';

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

/**
 * A child's profile: their wish list, and the adults who look after it.
 *
 * The one thing that makes this different from PetDetail — and it is the whole feature —
 * is that **carers are NOT blind to purchases here.** A pet's caretaker is deliberately
 * kept in the dark so the surprise survives; a child has no account, so there is nobody
 * to surprise, and two carers who cannot see what has been bought will buy it twice.
 *
 * So this screen passes no `forceOwnerView`, and `WishGiftCard` independently exempts any
 * wish carrying `child_id` from its owner branch. The API is the real gate
 * (Wish::isHiddenFromViewer); both clients simply render what it sends.
 */
export default function ChildDetail() {
    const { childId } = useParams();
    const [params] = useSearchParams();
    const groupId = params.get('group');
    const navigate = useNavigate();
    const { user } = useAuth();

    const [child, setChild] = useState(null);
    const [wishes, setWishes] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState(null);
    const [modal, setModal] = useState(null);
    const [busyCarer, setBusyCarer] = useState(null);

    const notify = (type, text) => setMessage({ type, text });

    const load = useCallback(async () => {
        try {
            const [c, w] = await Promise.all([getChild(childId), listChildWishes(childId)]);
            setChild(c);
            setWishes(w);
            // Members power the "add a carer" picker. A carer must already be in the
            // group (the API refuses otherwise), so this is the exact candidate set.
            if (groupId) {
                const g = await api.get(`/groups/${groupId}`);
                setMembers(g.data.members || []);
            }
        } catch (err) {
            setError(errText(err, 'Could not load this child.'));
        } finally {
            setLoading(false);
        }
    }, [childId, groupId]);

    useEffect(() => { load(); }, [load]);

    const canManage = isCarer(child, user?.id);

    const handleAddCarer = async (userId) => {
        setBusyCarer(userId);
        try {
            setChild(await addCarer(child.id, Number(userId)));
            notify('success', 'Carer added.');
        } catch (err) {
            notify('error', errText(err, 'Could not add that carer.'));
        } finally {
            setBusyCarer(null);
        }
    };

    const handleRemoveCarer = async (carer) => {
        const self = carer.id === user?.id;
        if (!window.confirm(
            self
                ? `Stop looking after ${child.name}'s list? You won't be able to add to it any more.`
                : `Remove ${carer.name} as a carer for ${child.name}?`
        )) return;
        setBusyCarer(carer.id);
        try {
            await removeCarer(child.id, carer.id);
            await load();
            notify('success', self ? 'You no longer look after this list.' : 'Carer removed.');
        } catch (err) {
            // The API refuses to remove the LAST carer (422) — surface its wording
            // rather than second-guessing the rule here.
            notify('error', errText(err, 'Could not remove that carer.'));
        } finally {
            setBusyCarer(null);
        }
    };

    if (loading) return <AppLayout><p className="text-gray-600">Loading…</p></AppLayout>;
    if (error) return <AppLayout><p className="text-red-600">{error}</p></AppLayout>;
    if (!child) return null;

    const bday = nextBirthday(child.birthday);
    const carers = child.carers || [];
    const carerIds = carers.map((c) => c.id);
    const candidates = members.filter((m) => !carerIds.includes(m.id));

    return (
        <AppLayout>
            {message && (
                <div className={`mb-4 p-3 rounded-md text-sm ${
                    message.type === 'error'
                        ? 'bg-red-50 border border-red-200 text-red-700'
                        : 'bg-green-50 border border-green-200 text-green-700'}`}>
                    {message.text}
                </div>
            )}

            <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-800" aria-label="Back">←</button>
                    <Avatar name={child.name} url={child.avatar_url} color={child.avatar_color} seed={child.id} size={48} />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{child.name}</h1>
                        {bday && (
                            <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                🎂 {bday.label} · {countdownLabel(bday.daysUntil)}
                            </span>
                        )}
                    </div>
                </div>
                {canManage && (
                    <button
                        onClick={() => setModal({})}
                        className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 text-sm font-medium whitespace-nowrap"
                    >
                        + Add a gift idea
                    </button>
                )}
            </div>

            {/* Carers — the point of the feature, so it sits above the list rather than
                buried in a settings screen. */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <h2 className="text-sm font-semibold text-gray-900 mb-2">Who looks after this list</h2>
                <div className="flex flex-wrap gap-2 mb-3">
                    {carers.map((c) => (
                        <span key={c.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-sm">
                            {c.name}{c.id === user?.id && <span className="text-gray-400 text-xs">(you)</span>}
                            {canManage && (
                                <button
                                    onClick={() => handleRemoveCarer(c)}
                                    disabled={busyCarer === c.id}
                                    className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                                    aria-label={`Remove ${c.name} as a carer`}
                                >
                                    ×
                                </button>
                            )}
                        </span>
                    ))}
                </div>

                {canManage && candidates.length > 0 && (
                    <select
                        value=""
                        onChange={(e) => e.target.value && handleAddCarer(e.target.value)}
                        className="text-sm px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="">+ Add someone from this group…</option>
                        {candidates.map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                        ))}
                    </select>
                )}

                <p className="text-xs text-gray-500 mt-2">
                    Carers add and edit this list — and all of you can see what&rsquo;s already
                    been bought, so nobody buys the same thing twice. Everyone else in the
                    group can shop from it.
                </p>
            </div>

            <h2 className="text-lg font-semibold text-gray-900 mb-3">{child.name}&rsquo;s list</h2>

            {wishes.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    Nothing on the list yet.
                </div>
            ) : (
                <div className="space-y-4">
                    {wishes.map((wish) => (
                        <WishGiftCard
                            key={wish.id}
                            wish={wish}
                            user={user}
                            // Deliberately NO forceOwnerView. Unlike the pet profile, a
                            // carer is meant to see claim and purchase state — that is
                            // the whole reason children are not simply pets.
                            notify={notify}
                            onShareChanged={load}
                        />
                    ))}
                </div>
            )}

            {modal && (
                <WishFormModal
                    wish={modal.wish}
                    lockedGroupId={groupId ? Number(groupId) : undefined}
                    lockedChildId={child.id}
                    childrenList={[child]}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); load(); }}
                    notify={notify}
                />
            )}
        </AppLayout>
    );
}
